import { useState } from 'react';
import { Copy, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import type { VirtualAccountSummary } from '@/types/wallet';

interface WalletOverviewCardProps {
  balance: number;
  account: VirtualAccountSummary | null;
  onFundClick: () => void;
  onRefresh: () => void;
  onGenerate?: () => void;
  refreshing?: boolean;
  generating?: boolean;
  fundingEnabled?: boolean;
}

export function WalletOverviewCard({
  balance,
  account,
  onFundClick,
  onRefresh,
  onGenerate,
  refreshing = false,
  generating = false,
  fundingEnabled = true,
}: WalletOverviewCardProps) {
  const [hidden, setHidden] = useState(() => sessionStorage.getItem('hideWalletBalance') === '1');
  const [copied, setCopied] = useState(false);

  function toggleHidden() {
    setHidden((prev) => {
      const next = !prev;
      sessionStorage.setItem('hideWalletBalance', next ? '1' : '0');
      return next;
    });
  }

  async function copyAccountNumber() {
    if (!account) return;
    await navigator.clipboard.writeText(account.accountNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const bankLabel = account ? (account.bankName.split('/')[0]?.trim() ?? account.bankName) : 'PalmPay';

  return (
    <div className="relative overflow-hidden rounded-2xl bg-brand-navy text-white shadow-card">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-brand" />
      <div className="pointer-events-none absolute -right-16 top-0 h-48 w-48 rounded-full bg-primary/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-8 h-40 w-40 rounded-full bg-secondary/15 blur-3xl" />

      <div className="relative grid grid-cols-2 divide-x divide-white/15">
        <div className="min-w-0 p-4 sm:p-5">
          <div className="flex items-center gap-1.5">
            <p className="text-xs font-semibold text-white">Wallet balance</p>
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              aria-label="Refresh balance"
              className="rounded-md p-0.5 text-white/80 transition hover:bg-white/10 hover:text-white disabled:opacity-50"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
            </button>
          </div>

          <div className="mt-2 flex items-center gap-2">
            <p className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
              {hidden
                ? '₦ ••••••'
                : `₦${balance.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`}
            </p>
            <button
              type="button"
              onClick={toggleHidden}
              aria-label={hidden ? 'Show balance' : 'Hide balance'}
              className="rounded-md p-0.5 text-white/80 transition hover:bg-white/10 hover:text-white"
            >
              {hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          <p className="mt-1 text-xs text-white/80">Available funds</p>
          <button
            type="button"
            onClick={onFundClick}
            className="mt-3 rounded-lg border border-white/30 bg-white/15 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/25"
          >
            Fund wallet
          </button>
        </div>

        <div className="min-w-0 p-4 sm:p-5">
          <p className="text-xs font-semibold text-white">Virtual account</p>

          {account ? (
            <>
              <div className="mt-2 flex items-center gap-1.5">
                <p className="font-mono text-xl font-bold tracking-wide text-white sm:text-2xl">{account.accountNumber}</p>
                <button
                  type="button"
                  onClick={() => void copyAccountNumber()}
                  aria-label="Copy account number"
                  className="rounded-md p-1 text-white/80 transition hover:bg-white/10 hover:text-white"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
              {copied && <p className="mt-0.5 text-xs font-semibold text-secondary">Copied!</p>}

              <div className="mt-3 space-y-2 border-t border-white/20 pt-3">
                <p className="text-base font-semibold leading-snug text-white">{bankLabel}</p>
                <p className="break-words text-sm leading-snug text-white/90 sm:text-base">{account.accountName}</p>
              </div>
            </>
          ) : (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-white/75">No virtual account yet</p>
              <Button
                size="sm"
                className="bg-white/15 text-white hover:bg-white/25"
                loading={generating}
                loadingText="Generating..."
                disabled={!fundingEnabled}
                onClick={onGenerate}
              >
                Generate account
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
